import React from "react";
import { useTranslation } from "react-i18next";

interface NotificationModalProps {
  isOpen: boolean;
  type: "success" | "error" | "warning";
  title?: string;
  message: string;
  onClose: () => void;
  autoCloseDelay?: number; // in milliseconds, 0 = no auto-close
}

export default function NotificationModal({
  isOpen,
  type,
  title,
  message,
  onClose,
  autoCloseDelay,
}: NotificationModalProps) {
  const { t } = useTranslation(['common']);
  // Error modals require manual close by default (no auto-close)
  // Success modals auto-close after 5 seconds by default
  const effectiveDelay = autoCloseDelay !== undefined
    ? autoCloseDelay
    : (type === "error" ? 0 : 5000);

  React.useEffect(() => {
    if (!isOpen || effectiveDelay === 0) return;

    const timer = setTimeout(() => {
      onClose();
    }, effectiveDelay);

    return () => clearTimeout(timer);
  }, [isOpen, effectiveDelay, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return "✅";
      case "error":
        return "❌";
      case "warning":
        return "⚠️";
      default:
        return "ℹ️";
    }
  };

  const getDefaultTitle = () => {
    switch (type) {
      case "success":
        return t('common:notifications.success', 'Success');
      case "error":
        return t('common:notifications.error', 'Error');
      case "warning":
        return t('common:notifications.warning', 'Warning');
      default:
        return t('common:modal.information', 'Information');
    }
  };

  return (
    <div className="modal-overlay">
      <div className={`notification-modal notification-modal-${type}`}>
        <div className="notification-modal-header">
          <div className="notification-modal-title-container">
            <span className="notification-modal-icon">{getIcon()}</span>
            <h2 className="notification-modal-title">
              {title || getDefaultTitle()}
            </h2>
          </div>
          <button
            className="notification-modal-close"
            onClick={onClose}
            aria-label={t('common:buttons.close', 'Close')}
          >
            ✕
          </button>
        </div>

        <div className="notification-modal-body">
          <p className="notification-modal-message">{message}</p>
        </div>

        <div className="notification-modal-footer">
          <button
            className="notification-modal-button notification-modal-button-primary"
            onClick={onClose}
          >
            {t('common:buttons.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
}
